import { useId } from 'react';

import { Info, TriangleAlert } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  type PushNotificationsStatus,
  PushNotificationsStatuses,
  usePushNotificationsSetting,
} from '@/modules/notifications/hooks';

import { SettingsPageWrapper } from '../../components/settings-page-wrapper';

const PushNotificationsHint = ({ status }: { status: PushNotificationsStatus }) => {
  const { t } = useTranslation(['authenticated.settings', 'authenticated.notifications']);

  if (status === PushNotificationsStatuses.Blocked) {
    return (
      <Alert variant="warning">
        <TriangleAlert className="h-4 w-4" />
        <AlertDescription className="text-xs">
          <Trans
            i18nKey="authenticated.notifications:deniedModal.howToEnable"
            components={{ bold: <strong /> }}
          />
        </AlertDescription>
      </Alert>
    );
  }

  if (status === PushNotificationsStatuses.Unsupported) {
    return (
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          {t('pages.notifications.unsupported')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <p className="flex items-center gap-2 text-xs text-muted-foreground">
      <Info className="h-4 w-4 shrink-0" />
      {t('pages.notifications.scopeHint')}
    </p>
  );
};

export const NotificationsPage = () => {
  const { t } = useTranslation('authenticated.settings');
  const switchId = useId();
  const { status, isPending, enable, disable } = usePushNotificationsSetting();

  const canToggle =
    status === PushNotificationsStatuses.Enabled || status === PushNotificationsStatuses.Disabled;
  const isDisabled = !canToggle || isPending;

  const handleCheckedChange = (isChecked: boolean) => (isChecked ? enable() : disable());

  return (
    <SettingsPageWrapper>
      <Card>
        <CardHeader>
          <CardTitle>{t('pages.notifications.title')}</CardTitle>
          <CardDescription>{t('pages.notifications.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 rounded-md border border-border px-3.5 py-3">
            <div className={cn('flex flex-1 flex-col gap-0.5', !canToggle && 'opacity-70')}>
              <label
                htmlFor={switchId}
                className={cn(
                  'text-sm font-semibold leading-5 text-foreground',
                  !canToggle && 'cursor-not-allowed'
                )}
              >
                {t('pages.notifications.push.label')}
              </label>
              <p className="text-xs font-normal leading-4 text-muted-foreground">
                {t('pages.notifications.push.description')}
              </p>
            </div>
            <Switch
              id={switchId}
              checked={status === PushNotificationsStatuses.Enabled}
              onCheckedChange={handleCheckedChange}
              disabled={isDisabled}
            />
          </div>
          <PushNotificationsHint status={status} />
        </CardContent>
      </Card>
    </SettingsPageWrapper>
  );
};
